import { PlanType } from 'api-validations/user-api'
import { Outlet } from 'react-router'
import { OrgLevelPermissions, useOrgInfo } from 'shared-ui/active-operation'
import { Forbidden403, Loading } from 'shared-ui/components'
import { useLocalization } from 'shared-ui/localization'
import { isNil } from 'utils'

/** Why a dealer-restricted route refuses an organization. */
export enum DealerRestrictedDenial {
  DealerOrg = 'dealer-org',
  NotAManager = 'not-a-manager'
}

type RestrictedOrgInfo = NonNullable<ReturnType<typeof useOrgInfo>>

/**
 * Whether a dealer-restricted route would render for this organization, and what
 * it objects to when it would not. Anything that sends a user to one of these
 * routes can ask first rather than landing them on a 403.
 */
export const dealerRestrictedDenial = (
  orgInfo: RestrictedOrgInfo
): DealerRestrictedDenial | undefined => {
  if (orgInfo.planType === PlanType.Dealer) return DealerRestrictedDenial.DealerOrg
  if (!(OrgLevelPermissions.canManage(orgInfo) || OrgLevelPermissions.isSupporter(orgInfo)))
    return DealerRestrictedDenial.NotAManager
  return undefined
}

/**
 * Route guard that restricts access for dealer orgs and non-manager/supporter users.
 * Use this to wrap any routes that should not be accessible to dealer organizations.
 */
export const DealerRestrictedGuard: React.FC = () => {
  const orgInfo = useOrgInfo()
  const { t } = useLocalization()

  if (isNil(orgInfo)) {
    return <Loading />
  }

  const denial = dealerRestrictedDenial(orgInfo)

  if (denial === DealerRestrictedDenial.DealerOrg) {
    return <Forbidden403 message={t(($) => $.dealer.notAvailableForDealers)} />
  }

  if (denial === DealerRestrictedDenial.NotAManager) {
    return (
      <Forbidden403
        message={t(($) => $['only-org-managers'], {
          ownerEmail: orgInfo.ownerEmail
        })}
      />
    )
  }

  return <Outlet />
}
